<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { Ban, ContactRound, Link2, Pencil, Plus, RefreshCw, Send, ShieldCheck } from 'lucide-vue-next'
import { ElMessage, ElMessageBox } from 'element-plus'
import { api, errorText, object, rows, type JsonRecord } from '../api'

const contacts = ref<JsonRecord[]>([])
const channels = ref<JsonRecord[]>([])
const loading = ref(false)
const saving = ref(false)
const error = ref('')
const dialog = ref(false)
const editingId = ref('')
const advanced = ref(false)
const form = reactive({ name: '', note: '', channel: '', receiver_id_type: 'open_id', receiver_id: '' })
const current = ref<JsonRecord>({})
const channelKeys = ref<string[]>([])
const bindingsOf = (contact: JsonRecord) => object(object(contact.config).bindings)
const contactStatus = (contact: JsonRecord) => Object.values(bindingsOf(contact)).some(value => String(object(value).bound_at ?? '').length > 0)

const linkDialog = ref(false)
const linkTitle = ref('')
const linkUrl = ref('')
const linkContactId = ref('')
const linkName = ref('')
const linkChannel = ref('')
const linkChannels = ref<string[]>([])
const bindingsMap = ref<JsonRecord>({})
const linkLoading = ref(false)

function openBindLink(contact: JsonRecord, preferredChannel = '') {
  const bound = bindingsOf(contact)
  const candidates = channels.value.filter(row => String(row.type ?? '') === 'feishu_app').map(row => String(row.key))
  if (!candidates.length) { ElMessage.warning('请先在「AI 客服」页给客服配置渠道应用（App ID / Secret）'); return }
  linkContactId.value = String(contact.id)
  linkName.value = String(contact.name ?? '')
  bindingsMap.value = bound
  linkChannels.value = candidates
  linkChannel.value = candidates.includes(preferredChannel) ? preferredChannel : candidates[0]
  linkUrl.value = ''
  linkTitle.value = ''
  linkDialog.value = true
  if (candidates.length === 1 || preferredChannel) void generateLink(linkChannel.value)
}
async function generateLink(channel: string) {
  linkLoading.value = true
  try {
    const result = object(await api.post(`/contacts/${linkContactId.value}/feishu-bind`, { channel }))
    const url = String(result.authorizeUrl ?? '')
    if (!url) { ElMessage.error('未获得授权链接'); return }
    linkUrl.value = url
    linkTitle.value = `把此链接发给「${linkName.value}」，在飞书打开并授权即可完成绑定`
    linkChannels.value = channels.value.filter(row => String(row.type ?? '').startsWith('feishu_app')).map(row => String(row.key))
    linkChannel.value = channel
    linkDialog.value = true
  } catch (cause) { ElMessage.error(errorText(cause)) } finally { linkLoading.value = false }
}
async function copyLink() {
  try { await navigator.clipboard.writeText(linkUrl.value); ElMessage.success('链接已复制') }
  catch { ElMessage.warning('浏览器不允许自动复制，请手动选中复制') }
}
async function refreshContact() {
  linkLoading.value = true
  try {
    await load()
    const contact = contacts.value.find(item => String(item.id) === linkContactId.value)
    const binding = contact ? object(bindingsOf(contact)[linkChannel.value]) : {}
    if (String(binding.bound_at ?? '').length > 0) { ElMessage.success('检测到绑定完成'); linkDialog.value = false }
    else ElMessage.info('尚未收到授权结果，请确认对方已在飞书打开链接并点击授权')
  } catch (cause) { ElMessage.error(errorText(cause)) } finally { linkLoading.value = false }
}
async function testContact(contact: JsonRecord) {
  const bound = bindingsOf(contact)
  const keys = Object.keys(bound).filter(key => String(object(bound[key]).bound_at ?? '').length > 0)
  if (!keys.length) { ElMessage.warning('该联系人还没有可用绑定'); return }
  try {
    await api.post(`/contacts/${String(contact.id)}/feishu-test`, { channel: keys[0] })
    ElMessage.success('测试消息已发送，请让本人在飞书确认收到')
  } catch (cause) { ElMessage.error(errorText(cause)) }
}

async function load() {
  loading.value = true; error.value = ''
  const results = await Promise.allSettled([api.get('/contacts'), api.get('/agents'), api.get('/enterprise')])
  if (results[0].status === 'fulfilled') contacts.value = rows(results[0].value); else error.value = `联系人：${errorText(results[0].reason)}`
  const byKey = new Map<string, JsonRecord>()
  if (results[1].status === 'fulfilled') {
    // 渠道应用已迁到 AI 客服配置：联系人可绑定的渠道 = 各客服配置的飞书渠道标识
    for (const agent of rows(results[1].value)) {
      const bot = object(object(agent.config).bot)
      const key = String(bot.channel ?? '').trim()
      if (!key || !String(bot.app_id ?? '').trim()) continue
      byKey.set(key, { key, type: 'feishu_app', enabled: true, agent: String(agent.name ?? agent.id) })
    }
  } else error.value += `${error.value ? '；' : ''}客服渠道：${errorText(results[1].reason)}`
  if (results[2].status === 'fulfilled') {
    // 旧企业渠道（含管理员通知机器人）继续可被联系人绑定：通知渠道后期也能承接导师答疑，
    // 但一个渠道同时最多只能服务一个客服，占用校验在客服保存时兜底。
    const raw = object(object(object(results[2].value).config).channels)
    for (const [key, value] of Object.entries(raw)) {
      const row = object(value)
      if (row.enabled === true && String(row.type ?? '').startsWith('feishu') && !byKey.has(key)) {
        byKey.set(key, { key, type: 'feishu_app', enabled: true, agent: '企业渠道' })
      }
    }
  }
  channels.value = Array.from(byKey.values())
  channelKeys.value = channels.value.map(row => String(row.key))
  loading.value = false
}
function openCreate() { editingId.value = ''; advanced.value = false; current.value = {}; Object.assign(form, { name: '', note: '', channel: '', receiver_id_type: 'open_id', receiver_id: '' }); dialog.value = true }
function openEdit(contact: JsonRecord) {
  editingId.value = String(contact.id); advanced.value = false; current.value = contact
  const bindings = bindingsOf(contact)
  const first = Object.entries(bindings)[0]
  const binding = first ? object(first[1]) : {}
  Object.assign(form, { name: String(contact.name ?? ''), note: String(object(contact.config).note ?? ''), channel: first?.[0] ?? '', receiver_id_type: String(binding.receiver_id_type ?? 'open_id'), receiver_id: '' })
  dialog.value = true
}
async function save() {
  if (!form.name.trim()) { ElMessage.warning('请填写联系人姓名'); return }
  const channel = form.channel
  const manualAddress = !!channel && !!form.receiver_id.trim()
  if (!editingId.value && !channel) { ElMessage.warning('请选择接收渠道（保存后会生成一键绑定链接）'); return }
  saving.value = true
  let savedContact: JsonRecord | null = null
  try {
    const oldConfig = object(current.value.config)
    const bindings = { ...object(oldConfig.bindings) }
    if (manualAddress) bindings[channel] = { receiver_id_type: form.receiver_id_type, receiver_id: form.receiver_id.trim() }
    const body = { name: form.name.trim(), config: { ...oldConfig, bindings, note: form.note.trim() } }
    if (editingId.value) savedContact = object(await api.put(`/contacts/${editingId.value}`, body))
    else savedContact = object(await api.post('/contacts', body))
    ElMessage.success(manualAddress ? '联系人已保存，地址由服务端加密保存' : '联系人已保存，请完成飞书授权绑定')
    dialog.value = false
    await load()
  } catch (cause) { ElMessage.error(errorText(cause)); return } finally { saving.value = false }

  const channelRow = channels.value.find(row => String(row.key) === channel)
  if (channel && String(channelRow?.type ?? '') === 'feishu_app') {
    const saved = contacts.value.find(item => String(item.id) === String(savedContact?.id)) ?? savedContact
    if (saved) {
      const binding = object(bindingsOf(saved)[channel])
      const addressWasManuallyEntered = manualAddress
      const needsOAuth = !String(binding.bound_at ?? '').length && !addressWasManuallyEntered
      if (needsOAuth) openBindLink(saved, channel)
    }
  }
}
async function remove(contact: JsonRecord) {
  try { await ElMessageBox.confirm(`确定停用「${String(contact.name)}」？相关客服路由应由管理员重新校验。`, '停用联系人', { type: 'warning' }); await api.delete(`/contacts/${String(contact.id)}`); ElMessage.success('联系人已停用'); await load() } catch (cause) { if (!(cause instanceof Error && cause.message === 'cancel')) ElMessage.error(errorText(cause)) }
}
onMounted(load)
</script>

<template>
  <div class="page-header"><div><h1 class="page-title">联系人</h1><p class="page-description">维护飞书等渠道的接收地址；联系人不登录企业端，可通过管理员分享的一键授权链接自助绑定。</p></div><div class="page-actions"><el-button :icon="RefreshCw" :loading="loading" @click="load">刷新</el-button><el-button type="primary" :icon="Plus" @click="openCreate">新增联系人</el-button></div></div>
  <div class="page-content"><div v-if="error" class="error-banner">{{ error }}</div><div class="panel"><div class="panel-head"><div><h2>联系人绑定</h2><p>接收地址仅展示脱敏绑定状态，密文不回传到浏览器。</p></div><ShieldCheck :size="17" class="muted"/></div>
    <div v-if="loading && !contacts.length" class="empty-state">正在读取联系人…</div><div v-else-if="!contacts.length" class="empty-state"><ContactRound :size="22"/><strong>暂无联系人</strong><span>由企业管理员录入联系人与已配置的渠道地址。</span><el-button type="primary" size="small" @click="openCreate">新增联系人</el-button></div>
    <div v-else class="table-scroll"><table class="data-table"><thead><tr><th>姓名</th><th>Snowflake ID</th><th>渠道绑定</th><th>备注</th><th>更新时间</th><th class="col-actions">操作</th></tr></thead><tbody><tr v-for="contact in contacts" :key="String(contact.id)"><td><div class="row-primary">{{ contact.name }}</div><div class="row-sub">{{ contactStatus(contact) ? '绑定就绪' : '未配置可用地址' }}</div></td><td class="mono">{{ contact.id }}</td><td><div class="inline-list"><span v-for="(binding, key) in bindingsOf(contact)" :key="key" class="status-tag" :class="object(binding).bound_at ? 'active' : 'pending'">{{ key }} · {{ object(binding).receiver_id_type ?? '未知类型' }}</span><span v-if="!Object.keys(bindingsOf(contact)).length" class="muted">未绑定</span></div></td><td><div class="row-sub">{{ object(contact.config).note || '—' }}</div></td><td>{{ contact.updated_at ?? '—' }}</td><td class="col-actions"><div class="row-actions"><el-button class="row-action" @click="openEdit(contact)"><Pencil :size="14" />编辑</el-button><el-button class="row-action primary" :loading="linkLoading" @click="openBindLink(contact)"><Link2 :size="14" />一键绑定飞书</el-button><el-button class="row-action" @click="testContact(contact)"><Send :size="14" />发测试</el-button><el-button class="row-action danger" @click="remove(contact)"><Ban :size="14" />停用</el-button></div></td></tr></tbody></table></div>
  </div><div class="helper-text">安全要求：接收地址仅在本系统服务端加密保存，浏览器不回显。答疑教学需先在「AI 客服」页给客服配置渠道应用；联系人无需注册或登录知应。</div></div>
  <el-dialog v-model="dialog" :title="editingId ? '编辑联系人' : '新增联系人'" width="620px" destroy-on-close>
    <el-form label-position="top"><div class="form-grid"><el-form-item label="联系人姓名" required><el-input v-model="form.name" maxlength="100" placeholder="姓名"/></el-form-item><el-form-item label="飞书渠道"><el-select v-model="form.channel" clearable placeholder="选择客服已配置的飞书渠道"><el-option v-for="row in channels.filter(item => String(item.type ?? '') === 'feishu_app')" :key="String(row.key)" :label="String(row.key)" :value="String(row.key)"/></el-select></el-form-item><el-form-item label="备注" class="span-two"><el-input v-model="form.note" type="textarea" :rows="2" maxlength="500" placeholder="负责范围或联系备注"/></el-form-item></div>
      <p v-if="!editingId" class="helper-text">保存后会生成一键授权链接，发给联系人本人在飞书打开即可完成绑定。</p>
      <div v-if="editingId" class="advanced-bind">
        <el-button link type="primary" @click="advanced = !advanced">高级：手动填写接收地址（{{ advanced ? '收起' : '展开' }}）</el-button>
        <div v-if="advanced" class="form-grid">
          <el-form-item label="接收地址类型"><el-select v-model="form.receiver_id_type"><el-option v-for="value in ['open_id','user_id','union_id','email','chat_id']" :key="value" :label="value" :value="value"/></el-select></el-form-item>
          <el-form-item label="飞书接收地址"><el-input v-model="form.receiver_id" type="password" show-password autocomplete="off" placeholder="留空保持不变"/><span class="credential-note">已知道对方 open_id / 邮箱时可直接录入；否则用「一键绑定飞书」更省事。</span></el-form-item>
        </div>
      </div>
    </el-form>
    <template #footer><div class="dialog-actions"><el-button @click="dialog=false">取消</el-button><el-button type="primary" :loading="saving" @click="save">{{ editingId ? '保存联系人' : '保存并生成绑定链接' }}</el-button></div></template>
  </el-dialog>
  <el-dialog v-model="linkDialog" title="一键绑定飞书" width="560px">
    <el-form label-position="top">
      <el-form-item label="绑定渠道">
        <el-select v-model="linkChannel" style="width:100%" :disabled="!!linkUrl" @change="linkUrl=''">
          <el-option v-for="key in linkChannels" :key="key" :label="`${key}${object(bindingsMap[key] ?? {}).bound_at ? '（已绑定，重新授权将覆盖）' : ''}`" :value="key"/>
        </el-select>
      </el-form-item>
    </el-form>
    <template v-if="linkUrl">
      <p class="helper-text">{{ linkTitle }}</p>
      <el-input :model-value="linkUrl" readonly type="textarea" :rows="3" class="mono bind-link"/>
      <p class="helper-text">有效期 15 分钟、仅可使用一次。对方在飞书内打开链接并点击「授权」即完成绑定；完成后点下方「检查绑定结果」。</p>
    </template>
    <div v-else class="helper-text">选择渠道后点击「生成授权链接」，再把链接通过微信/飞书转发给联系人本人。</div>
    <template #footer><div class="dialog-actions">
      <el-button @click="linkDialog=false">关闭</el-button>
      <el-button v-if="linkUrl" @click="refreshContact" :loading="linkLoading">检查绑定结果</el-button>
      <el-button v-if="!linkUrl" type="primary" :loading="linkLoading" @click="generateLink(linkChannel)">生成授权链接</el-button>
      <el-button v-else type="primary" @click="copyLink">复制链接</el-button>
    </div></template>
  </el-dialog>
</template>

<style scoped>
.bind-link :deep(textarea) { font-family: ui-monospace, Consolas, monospace; font-size: 12px; }
.advanced-bind { margin: 6px 0 10px; }
.advanced-bind .form-grid { margin-top: 8px; }
</style>
